"use client";

// ── Practice hints, beside the statement (`EX-HNT01`, `docs/27` §10.1.7) ──────
// Board `PracticeHintBar` on the Wave 34 canvas. After a wrong verdict the
// reader takes one step at a time: the idea, the approach, the case they
// missed. The setter's own hints come first and are free; AI writes the steps
// after them for one credit each. Only the next step is offered, and a step
// once shown stays shown. While a contest or an exam mounting the problem is
// in its window the server sends no text and the panel says why.

import { forwardRef, useRef } from "react";
import { Lightbulb, Loader2, Lock } from "lucide-react";
import { useTranslations } from "next-intl";

import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { usePracticeHints, useRevealPracticeHint } from "../../hooks";
import type { PracticeHintStep, PracticeHints } from "../../types";

/** Errors after which the next click needs a new key: the server closed the old operation. */
const NEW_KEY_AFTER = new Set(["AI_ERROR", "AI_UNAVAILABLE", "AI_GENERATION_PRIOR_FAILED", "AI_REPLAY_RESULT_UNAVAILABLE"]);

const ERROR_KEYS: Readonly<Record<string, string>> = {
  INSUFFICIENT_AI_CREDITS: "errorCredits",
  PLAN_LIMIT_EXCEEDED: "errorCredits",
  AI_UNAVAILABLE: "errorUnavailable",
  AI_ERROR: "errorFailed",
  AI_GENERATION_PRIOR_FAILED: "errorFailed",
  AI_REPLAY_RESULT_UNAVAILABLE: "errorFailed",
  AI_GENERATION_IN_FLIGHT: "errorInFlight",
  AI_CONFIRMATION_PENDING: "errorPending",
  JUDGE_PRACTICE_HINT_OUT_OF_ORDER: "errorOrder",
  RATE_LIMITED: "errorRateLimited",
};

export function practiceHintErrorKey(error: unknown): string | null {
  if (!(error instanceof ApiError) || !error.code) return null;
  return ERROR_KEYS[error.code] ?? null;
}

/** The step the reader may open next: the first one not shown. */
export function nextHintStep(hints: PracticeHints | undefined): number | null {
  const next = hints?.steps.find((step) => !step.shown);
  return next ? next.n : null;
}

/** Whether a problem's setter wrote any hint, so AI's label says it follows them. */
function setterWroteAny(hints: PracticeHints): boolean {
  return hints.steps.some((step) => step.source === "setter");
}

export type PracticeHintPanelProps = {
  slug: string;
  locale: "en" | "bn";
  /**
   * The reader's wrong submission a new step follows, or `null` when the
   * shown verdict is not a wrong one. Steps already shown stay readable.
   */
  wrongSubmissionId: string | null;
  /**
   * Whether to ask the server at all: a reader with no submission on this
   * problem has neither a wrong verdict nor a step taken, so the read would
   * only ever come back empty.
   */
  enabled: boolean;
};

export const PracticeHintPanel = forwardRef<HTMLElement, PracticeHintPanelProps>(function PracticeHintPanel(
  { slug, locale, wrongSubmissionId, enabled },
  ref,
) {
  const t = useTranslations("judge.hints");
  const hintsQuery = usePracticeHints(slug, locale, enabled || wrongSubmissionId !== null);
  const reveal = useRevealPracticeHint(slug, locale);
  // One key per deliberate click on an AI step; a retry keeps it unless the
  // server closed the operation, so a retry is never charged twice.
  const keyRef = useRef<{ step: number; key: string } | null>(null);

  const hints = hintsQuery.data;
  const anyShown = hints?.steps.some((step) => step.shown) ?? false;
  // Nothing to say until there is a wrong verdict or a step already taken.
  if (!wrongSubmissionId && !anyShown) return null;

  const next = nextHintStep(hints);
  const errorKey = practiceHintErrorKey(reveal.error);
  const errorMessage = reveal.isError
    ? errorKey
      ? t(errorKey)
      : reveal.error instanceof ApiError
        ? reveal.error.message
        : t("errorFailed")
    : null;

  function show(step: PracticeHintStep) {
    if (!wrongSubmissionId || reveal.isPending) return;
    let idempotencyKey: string | undefined;
    if (step.source === "ai") {
      const previous = keyRef.current;
      const code = reveal.error instanceof ApiError ? reveal.error.code : undefined;
      const reuse = previous?.step === step.n && reveal.isError && !(code && NEW_KEY_AFTER.has(code));
      idempotencyKey = reuse && previous ? previous.key : `phnt_${crypto.randomUUID()}`;
      keyRef.current = { step: step.n, key: idempotencyKey };
    }
    reveal.mutate({
      step: step.n,
      submissionId: wrongSubmissionId,
      locale,
      ...(idempotencyKey ? { idempotencyKey } : {}),
    });
  }

  return (
    <section
      ref={ref}
      id="practice-hints"
      tabIndex={-1}
      aria-labelledby="practice-hints-title"
      data-testid="practice-hints"
      className="mb-5 flex flex-col gap-3 rounded-[14px] border border-neutral-200 bg-white p-4 outline-none sm:p-[18px]"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <Lightbulb className="h-[18px] w-[18px] text-primary" aria-hidden />
          <h2 id="practice-hints-title" className="text-[17px] font-bold text-neutral-900">
            {t("title")}
          </h2>
          <span className="rounded-[5px] bg-[#FDECE5] px-[5px] py-[3px] font-mono text-[10.5px] font-bold uppercase leading-none tracking-[0.04em] text-[#C93D12]">
            {t("newBadge")}
          </span>
        </div>
        <span className="text-[12.5px] text-neutral-500">{t("neverWhole")}</span>
      </div>

      {hintsQuery.isPending ? (
        <div className="flex items-center gap-2 py-4 text-sm text-neutral-500">
          <Loader2 className="h-4 w-4 motion-safe:animate-spin" aria-hidden />
        </div>
      ) : hintsQuery.isError || !hints ? (
        <div role="alert" className="flex items-center justify-between gap-3 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-800">
          <span>{t("loadFailed")}</span>
          <Button type="button" variant="outline" size="sm" onClick={() => void hintsQuery.refetch()}>
            {t("retry")}
          </Button>
        </div>
      ) : hints.locked ? (
        <p role="status" className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm leading-relaxed text-amber-900">
          <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          {hints.locked.reason === "contest_running"
            ? t("lockedContest")
            : hints.locked.reason === "virtual_running"
              ? t("lockedVirtual")
              : t("lockedExam")}
        </p>
      ) : (
        <ol className="flex flex-col gap-3">
          {hints.steps.map((step) => {
            const isNext = step.n === next;
            const pending = reveal.isPending && reveal.variables?.step === step.n;
            const sourceLabel =
              step.source === "setter"
                ? t("sourceSetter")
                : setterWroteAny(hints)
                  ? t("sourceAiAfterSetter")
                  : t("sourceAi");
            return (
              <li
                key={step.n}
                data-testid={`hint-step-${step.n}`}
                className={cn(
                  "flex flex-col gap-2 rounded-xl border p-3.5",
                  step.shown ? "border-[#CFEBDD] bg-[#F7FCF9]" : "border-neutral-200 bg-neutral-50",
                )}
              >
                <div className="flex items-center justify-between gap-2.5">
                  <span className="flex items-center gap-2.5">
                    <span
                      aria-hidden
                      className={cn(
                        "inline-flex h-6 w-6 items-center justify-center rounded-[7px] text-xs font-bold tabular-nums",
                        step.shown ? "bg-[#12855A] text-white" : "bg-neutral-100 text-neutral-500",
                      )}
                    >
                      {step.n}
                    </span>
                    <b className="text-[14.5px] font-semibold text-neutral-900">{t(`kind_${step.kind}`)}</b>
                  </span>
                  {step.shown ? (
                    <span className={cn("text-xs", step.source === "setter" ? "text-[#047857]" : "text-[#1D4ED8]")}>
                      {sourceLabel}
                    </span>
                  ) : null}
                </div>
                {step.shown ? (
                  <p className="whitespace-pre-wrap text-[14.5px] leading-[1.65] text-neutral-700">
                    {step.text ?? t("removed")}
                  </p>
                ) : (
                  <div className="flex items-center justify-between gap-2.5">
                    <span className="text-[13px] text-neutral-500">
                      {step.cost === 0 ? t("costFree") : t("costCredits", { count: step.cost })}
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => show(step)}
                      disabled={!isNext || !wrongSubmissionId || reveal.isPending}
                      aria-busy={pending}
                    >
                      {pending ? <Loader2 className="h-3.5 w-3.5 motion-safe:animate-spin" aria-hidden /> : null}
                      {pending ? t("writing") : t("show", { n: step.n })}
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {errorMessage ? (
        <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-800">
          {errorMessage}
        </p>
      ) : null}

      <p className="text-[12.5px] leading-normal text-neutral-500">{t("footer")}</p>
    </section>
  );
});
